import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import type { Rim } from "@/lib/db/schema";
import { useSession } from "@/lib/auth/client";

interface RimsFilters {
  style?: string;
  finish?: string;
  freepreview?: boolean;
}

interface RimWithCount extends Rim {
  generationsCount: number;
}

export function useRims(
  filters?: RimsFilters,
  options?: Omit<UseQueryOptions<RimWithCount[]>, "queryKey" | "queryFn">
) {
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;

  return useQuery<RimWithCount[]>({
    queryKey: ["rims", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.style) params.set("style", filters.style);
      if (filters?.finish) params.set("finish", filters.finish);
      if (filters?.freepreview) params.set("freepreview", "true");

      const url = `/api/rims${params.toString() ? `?${params}` : ""}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch rims");
      }
      return response.json();
    },
    enabled: isAuthenticated && (options?.enabled ?? true), // Only fetch when user is authenticated
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    ...options,
  });
}
